import { useState, useRef } from 'react'
import { useSearchParams } from 'react-router'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useTranslate } from '@repo/unicity'
import { hotFormatRawAtom } from '@/core/store/hotFormat.store'
import { omanFormatDataAtom, omanSelectedShippingMethodAtom, isLoadingOmanFormatAtom, omanDeliveryFreightAtom } from '../../../stores/oman.store'
import { syncOmanShippingMethodFormat } from '../../../services/oman.api'
import { getHydraFreightAmount } from '../../../utils/oman-format.mapper'
import { hasDeliveryAddress } from '../address-display.helpers'
import { useNavigateToShipping } from './useNavigateToShipping'
import type { TOmanShippingMethodId } from '../../../types/oman.types'

export const useShippingMethodChange = () => {
    const { translate } = useTranslate()
    const [searchParams] = useSearchParams()
    const goToShipping = useNavigateToShipping()

    const [selectedMethod, setSelectedMethod] = useAtom(omanSelectedShippingMethodAtom)
    const formatData = useAtomValue(omanFormatDataAtom)
    const hotFormatRaw = useAtomValue(hotFormatRawAtom)
    const deliveryFreight = useAtomValue(omanDeliveryFreightAtom)
    const setFormatData = useSetAtom(omanFormatDataAtom)
    const setDeliveryFreight = useSetAtom(omanDeliveryFreightAtom)
    const setLoadingFormat = useSetAtom(isLoadingOmanFormatAtom)

    const [modalOpen, setModalOpen] = useState(false)
    const [draftMethod, setDraftMethod] = useState<TOmanShippingMethodId>(selectedMethod)
    const originalMethodRef = useRef<TOmanShippingMethodId | null>(null)

    const hydraFreight = getHydraFreightAmount(formatData)
    const displayFreight = selectedMethod === 'delivery' ? hydraFreight || deliveryFreight || formatData?.freight || 0 : deliveryFreight

    const currentUuid = searchParams.get('uuid')
    const label =
        selectedMethod === 'delivery'
            ? `${translate('economy')} - ${displayFreight ? `${displayFreight} OMR` : translate('free')}`
            : `${translate('pickup_oman_office')} - ${translate('free').toUpperCase()}`

    const handleOpenModal = () => {
        originalMethodRef.current = selectedMethod
        setDraftMethod(selectedMethod)
        setModalOpen(true)
    }

    const handleConfirm = async () => {
        const original = originalMethodRef.current
        const nextMethod = draftMethod

        if (!currentUuid || !original || original === nextMethod) {
            setModalOpen(false)
            originalMethodRef.current = null
            setDraftMethod(selectedMethod)
            return
        }

        if (original === 'pickup' && nextMethod === 'delivery' && !hasDeliveryAddress(hotFormatRaw)) {
            setModalOpen(false)
            originalMethodRef.current = null
            goToShipping('delivery')
            return
        }

        setModalOpen(false)
        originalMethodRef.current = null
        setLoadingFormat(true)
        try {
            const rawFormat = await syncOmanShippingMethodFormat(currentUuid, nextMethod, true)
            setFormatData({ ...rawFormat, uuid: currentUuid })
            setSelectedMethod(nextMethod)
            if (nextMethod !== 'pickup') {
                setDeliveryFreight(getHydraFreightAmount(rawFormat))
            }
        } catch {
            setDraftMethod(original)
        } finally {
            setLoadingFormat(false)
        }
    }

    const handleCloseBackdrop = () => {
        setDraftMethod(originalMethodRef.current ?? selectedMethod)
        setModalOpen(false)
        originalMethodRef.current = null
    }

    return {
        modalOpen,
        draftMethod,
        setDraftMethod,
        label,
        handleOpenModal,
        handleConfirm,
        handleCloseBackdrop,
        formatData
    }
}
